import type { DesiredTrendlineDirection, TrendColor, TrendDirection } from './comparison-table.type';
import { TREND_COLORS } from './comparison-table.type';

/**
 * Determines the direction of a trend (up, down, or flat).
 * @param {number} currentValue The current value
 * @param {number} previousValue The previous value to compare against
 * @returns {TrendDirection} The trend direction
 */
function getTrendDirection(currentValue: number, previousValue: number): TrendDirection {
  if (currentValue > previousValue) {
    return 'up';
  } else if (currentValue < previousValue) {
    return 'down';
  }
  return 'flat';
}

/**
 * Determines the color to display for a trend based on the desired direction.
 *
 * Color logic:
 * - Increasing + UP direction = Green (good)
 * - Increasing + DOWN direction = Red (bad)
 * - Decreasing + UP direction = Red (bad)
 * - Decreasing + DOWN direction = Green (good)
 * - Flat = Gray (neutral)
 *
 * @param {number} currentValue The current value
 * @param {number} previousValue The previous value to compare against
 * @param {DesiredTrendlineDirection | undefined} desiredDirection The desired trendline direction ('up' or 'down'). Defaults to 'up'.
 * @returns {TrendColor} The hex color to use for the trend indicator
 */
function getTrendColor(
  currentValue: number,
  previousValue: number,
  desiredDirection: DesiredTrendlineDirection | undefined,
): TrendColor {
  const direction = getTrendDirection(currentValue, previousValue);
  const effectiveDesiredDirection = desiredDirection ?? 'up';

  if (direction === 'flat') {
    return TREND_COLORS.neutral;
  }

  if (direction === 'up') {
    // Value is increasing
    return effectiveDesiredDirection === 'up' ? TREND_COLORS.good : TREND_COLORS.bad;
  }

  // Value is decreasing
  return effectiveDesiredDirection === 'down' ? TREND_COLORS.good : TREND_COLORS.bad;
}

export { getTrendColor, getTrendDirection, TREND_COLORS };
